import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { IconButton } from 'ksnm-common-ui/lib/IconButton';
import { Notification } from 'ksnm-common-ui/lib/Notification';

import { RoutePath } from '@components/AppRouter/constants';
import { usePermission } from '@src/utils/hooks/usePermission';
import { passingControlStore } from '@stores/passingControlStore';

import { StartLogsPageRouteAction } from '../../../Logs/constants';

import styles from '../../PublicationRegister/PublicationRegister.module.scss';

interface IActionButton {
  organizationCode: string;
  update: () => void;
  toggleAddControlDrawer: () => void;
}

export const ActionButton = observer(({ organizationCode, update, toggleAddControlDrawer }: IActionButton) => {
  const { selected, runControls, getControlReportByOrganizationCode } = passingControlStore;
  const { selectRows, rows = [] } = selected || {};

  const { push } = useHistory();

  const CmRunControl = usePermission('CmRunControl');
  const CmControlVersionReport = usePermission('CmControlVersionReport');

  const showNotification = useCallback(
    (text) => {
      const onGo = () => {
        push(RoutePath.LOGS, { action: StartLogsPageRouteAction.RUNNING_CONTROLS });
        Notification.close('showNotification');
      };

      Notification({
        message: (
          <>
            {text}{' '}
            <span onClick={onGo} className={styles.link}>
              Мониторинг фоновых процессов
            </span>
          </>
        ),
        type: 'success',
        key: 'showNotification',
      });
    },
    [push],
  );

  const onRunControls = useCallback(async () => {
    // берем ctrlVersionCode из выделенных контролей
    const ctrlVersionCodes = rows.map((item) => item.ctrlVersCode + '');

    const result = await runControls(rows[0].reportPackageCodeCm + '', ctrlVersionCodes);

    if (result) showNotification('Расчет контрольных процедур запущен. Результат будет отображён в разделе');
  }, [runControls, rows, showNotification]);

  // можем выбрать либо один пакет, либо любое кол-во групп контролей и самих контролей в рамках одного пакета
  const disableRun = useCallback(() => {
    let out = true;
    const selectedGroups = selectRows?.selectedGroups;

    if (selectedGroups?.length) {
      if (selectedGroups.length === 1) {
        if (selectedGroups[0].length <= 3) return true;

        const len = selectedGroups[0]?.length;
        out = !(len === 4 || len === 5);
      } else {
        const [, , , selectPackage, selectGroup] = selectedGroups[0];
        out = selectedGroups.some((item) => item.at(3) !== selectPackage || item.at(4) !== selectGroup);
      }
    }

    if (rows.length > 0) {
      const firstCode = rows[0]?.reportPackageCodeCm;
      out = !rows.every((item) => item?.reportPackageCodeCm === firstCode);
    }

    return out;
  }, [rows, selectRows?.selectedGroups]);

  const onRefresh = useCallback(async () => {
    await getControlReportByOrganizationCode(organizationCode);
    update();
  }, [getControlReportByOrganizationCode, organizationCode, update]);

  return (
    <>
      <IconButton
        key="create"
        iconName="add"
        title="Добавить КП в пакет"
        disabled={!CmControlVersionReport.CREATE}
        onClick={toggleAddControlDrawer}
      />
      <IconButton key="refresh" iconName="refresh" title="Обновить" onClick={onRefresh} />
      <IconButton
        key="run"
        iconName="postAdd"
        title="Выполнить расчет"
        disabled={!CmRunControl.UPDATE || disableRun()}
        onClick={onRunControls}
      />
    </>
  );
});
